import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { createContext, ReactNode, use, useCallback, useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import { ScrapEyesEventWithReviewData } from "components/manualReview/contexts/ManualReviewEventContext";
import { VideoTimeline } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimeline";
import { useVideoControl } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useTimelineZoom } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { VerificationChange } from "./utils/editScope";
import { formatUtcTimeRange } from "./utils/formatters";

dayjs.extend(utc);

export interface VerificationPreviewPeriod {
  start: Dayjs;
  end: Dayjs;
  change: VerificationChange;
}

export interface VerificationPreviewRequest {
  windowStart: Dayjs;
  windowEnd: Dayjs;
  periods: VerificationPreviewPeriod[];
  events: ScrapEyesEventWithReviewData[];
  modifiedEventIds: Set<string>;
}

type PreviewFn = (request: VerificationPreviewRequest) => Promise<boolean>;

const VerificationPreviewContext = createContext<PreviewFn | null>(null);

const CHANGE_COLOR: Record<VerificationChange, string> = {
  newlyVerified: "warning.main",
  reVerified: "success.main",
  unchanged: "grey.400",
};

const CHANGE_LABEL_KEY: Record<VerificationChange, string> = {
  newlyVerified: "newlyVerifiedLabel",
  reVerified: "reVerifiedLabel",
  unchanged: "unchangedLabel",
};

const CHANGE_OPACITY = (change: VerificationChange): number =>
  change === "unchanged" ? 0.25 : 0.6;

const formatRange = (period: VerificationPreviewPeriod): string =>
  formatUtcTimeRange(period.start, period.end);

const PreviewPeriodBars = ({
  periods,
  videoStart,
  duration,
}: {
  periods: VerificationPreviewPeriod[];
  videoStart: Dayjs;
  duration: number;
}) => {
  const zoomContext = useTimelineZoom();
  const visibleStart = zoomContext?.visibleStart ?? videoStart;
  const visibleDuration = zoomContext?.duration ?? duration;
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 6,
        overflow: "hidden",
      }}
    >
      {periods.map((period) => {
        const leftPercent =
          (period.start.diff(visibleStart, "second") / visibleDuration) * 100;
        const widthPercent =
          (period.end.diff(period.start, "second") / visibleDuration) * 100;
        return (
          <Tooltip
            key={period.start.valueOf()}
            title={formatRange(period)}
            arrow
          >
            <Box
              sx={{
                position: "absolute",
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
                height: "100%",
                bgcolor: CHANGE_COLOR[period.change],
                opacity: CHANGE_OPACITY(period.change),
                borderRadius: 0.5,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

const PreviewTimeline = ({
  request,
}: {
  request: VerificationPreviewRequest;
}) => {
  const { state, actions } = useVideoControl();
  const windowSeconds = request.windowEnd.diff(request.windowStart, "second");
  return (
    <Box sx={{ position: "relative" }}>
      <VideoTimeline
        currentTime={state.currentTime}
        duration={windowSeconds}
        events={request.events}
        onSeek={actions.seekTo}
        videoStart={request.windowStart}
        modifiedEventIds={request.modifiedEventIds}
      >
        <PreviewPeriodBars
          periods={request.periods}
          videoStart={request.windowStart}
          duration={windowSeconds}
        />
      </VideoTimeline>
    </Box>
  );
};

const LegendSwatch = ({ change }: { change: VerificationChange }) => {
  const { t } = useTenantTranslation();
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: 0.5,
          bgcolor: CHANGE_COLOR[change],
          opacity: CHANGE_OPACITY(change),
        }}
      />
      <Typography variant="body2">{t(CHANGE_LABEL_KEY[change])}</Typography>
    </Stack>
  );
};

export const VerificationPreviewProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTenantTranslation();
  const [request, setRequest] = useState<VerificationPreviewRequest | null>(
    null
  );
  const [resolver, setResolver] = useState<
    ((confirmed: boolean) => void) | null
  >(null);

  const previewVerification = useCallback<PreviewFn>((nextRequest) => {
    return new Promise<boolean>((resolve) => {
      setRequest(nextRequest);
      setResolver(() => resolve);
    });
  }, []);

  const settle = useCallback(
    (confirmed: boolean) => {
      resolver?.(confirmed);
      setResolver(null);
      setRequest(null);
    },
    [resolver]
  );

  const newlyVerified =
    request?.periods.filter((period) => period.change === "newlyVerified") ??
    [];
  const reVerified =
    request?.periods.filter((period) => period.change === "reVerified") ?? [];
  const hasUnchanged =
    request?.periods.some((period) => period.change === "unchanged") ?? false;

  return (
    <VerificationPreviewContext value={previewVerification}>
      {children}
      <Dialog
        open={request !== null}
        onClose={() => {
          settle(false);
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{t("verificationPreviewTitle")}</DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          {request ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {newlyVerified.length > 0 ? (
                <Stack spacing={0.5}>
                  <Typography color="warning.main">
                    {t("willNewlyVerifyPeriods", {
                      count: newlyVerified.length,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {newlyVerified.map(formatRange).join(", ")}
                  </Typography>
                </Stack>
              ) : null}
              {reVerified.length > 0 ? (
                <Stack spacing={0.5}>
                  <Typography>
                    {t("willReVerifyPeriods", { count: reVerified.length })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reVerified.map(formatRange).join(", ")}
                  </Typography>
                </Stack>
              ) : null}
              <PreviewTimeline request={request} />
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {newlyVerified.length > 0 ? (
                  <LegendSwatch change="newlyVerified" />
                ) : null}
                {reVerified.length > 0 ? (
                  <LegendSwatch change="reVerified" />
                ) : null}
                {hasUnchanged ? <LegendSwatch change="unchanged" /> : null}
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              settle(false);
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              settle(true);
            }}
            variant="contained"
            color="primary"
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </VerificationPreviewContext>
  );
};

export const useVerificationPreview = (): PreviewFn => {
  const context = use(VerificationPreviewContext);
  if (!context) {
    throw new Error(
      "useVerificationPreview must be used within a VerificationPreviewProvider"
    );
  }
  return context;
};
